import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Bell,
  Calendar,
  Check,
  X,
  Trash2,
  ArrowRight,
  AlertTriangle,
  Receipt,
  Wallet,
  ShoppingCart,
  CheckSquare,
} from 'lucide-react';
import { useSystemNotifications } from '@/hooks/useSystemNotifications';
import { SystemNotificationEvent } from '@/api/notifications.api';
import { useLocaleFormat } from '@/hooks/useLocaleFormat';
import type { LocaleFormatter } from '@/lib/locale-format';

/**
 * @file SystemNotificationToasts.tsx
 * @description SysNot-1: ephemeral toast stack mounted in Layout. Shows
 * the most recent N events from the deterministic system-notifications
 * channel, auto-dismisses after 6 seconds, manually dismissible.
 *
 * @why_dedicated_component
 * Toasts here are NOT the LLM chat reply — they confirm what the system
 * actually did (reminder created, memory updated, agent invoked). The
 * separation is intentional: the user sees the truth (this toast) even
 * if Master's LLM phrases the chat reply vaguely or gets it wrong.
 *
 * @why_no_external_lib
 * Adding react-hot-toast / sonner just for this would bloat the bundle.
 * Native fixed-position stack with Tailwind is ~80 LOC.
 */
const AUTO_DISMISS_MS = 6_000;

export function SystemNotificationToasts() {
  const { events, dismiss } = useSystemNotifications();

  // Auto-dismiss timer per event.
  useEffect(() => {
    if (events.length === 0) return;
    const timers = events.map((e) =>
      setTimeout(() => dismiss(e.uid), AUTO_DISMISS_MS),
    );
    return () => timers.forEach((t) => clearTimeout(t));
  }, [events, dismiss]);

  if (events.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {events.map((event) => (
        <ToastCard key={event.uid} event={event} onDismiss={() => dismiss(event.uid)} />
      ))}
    </div>
  );
}

function ToastCard({
  event,
  onDismiss,
}: {
  event: SystemNotificationEvent & { uid: string };
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useLocaleFormat();
  const description = describeEvent(event, fmt, t);
  if (!description) return null; // S4-45 HIGH-2: unrecognized type/action → silent drop
  const { icon, title, body, accent } = description;

  return (
    <div
      role="status"
      className={`rounded-lg shadow-lg border ${accent} bg-white dark:bg-gray-800 p-3 pr-10 relative animate-in slide-in-from-right`}
    >
      <button
        onClick={onDismiss}
        aria-label={t('common.close')}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-2">
        <div className="mt-0.5">{icon}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</p>
          {body && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{body}</p>}
        </div>
      </div>
    </div>
  );
}

interface ToastDescription {
  icon: JSX.Element;
  title: string;
  body?: string;
  accent: string;
}

/**
 * Машинный ISO-8601 момент (`YYYY-MM-DDThh:mm…`, парсится `Date`), а не текст для показа.
 * @contract Замкнутая форма ISO date-time, не перечень: локализованная строка
 *   продюсера («26 сент., 13:00») под неё не подпадает и показывается как есть.
 */
function isIsoInstant(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) && !Number.isNaN(Date.parse(value));
}

/**
 * Backend-SSOT types that this component knows are REAL but does not (yet)
 * render richly. Mirrors `SYSTEM_NOTIFICATION_TYPES`
 * (`libs/common/src/enums/system-notification-type.enum.ts`) for the types
 * that have no dedicated branch above.
 *
 * @why_not_a_full_mirror The frontend cannot import `@ecosystem/common`
 * (separate Vite build, no workspace alias — see S4-98 for closing that
 * split properly). Until then this map is the seam that keeps a live
 * backend type from silently disappearing on the web panel: adding a
 * branch above is the upgrade path, adding a line here is the floor.
 */
// S4-35: значения — ключи локали (заголовок тоста берёт `t()` при рендере).
// S4-94: `calendar` получил собственную ветку в `describeEvent` (название +
// время) и из пола убран; `scheduled` пока остаётся на полу.
const SSOT_TYPE_TITLES: Record<string, string> = {
  scheduled: 'notifications.ssot.scheduled',
};

/**
 * Translate a producer event into the user-visible toast shape. Branches
 * by `(type, action)`. Unknown combinations return `null` (silent drop) —
 * S4-45 HIGH-2: a generic "system event" fallback previously leaked raw
 * internal `type/action` strings into the UI whenever a new SSOT
 * notification type shipped without a matching frontend branch.
 */
function describeEvent(
  event: SystemNotificationEvent,
  fmt: LocaleFormatter,
  t: TFunction,
): ToastDescription | null {
  const { type, action, payload } = event;

  if (type === 'reminder') {
    if (action === 'created') {
      const title =
        (payload.title as string | undefined) ?? t('notifications.reminder.fallbackTitle');
      const when = payload.scheduledAt as string | undefined;
      return {
        icon: <Bell className="h-4 w-4 text-blue-500" />,
        title: t('notifications.reminder.created'),
        body: when
          ? t('notifications.reminder.createdBodyWhen', { title, when })
          : t('notifications.reminder.createdBody', { title }),
        accent: 'border-blue-200 dark:border-blue-800',
      };
    }
    if (action === 'cancelled') {
      return {
        icon: <Trash2 className="h-4 w-4 text-orange-500" />,
        title: t('notifications.reminder.cancelled'),
        body: payload.reminderId ? String(payload.reminderId) : undefined,
        accent: 'border-orange-200 dark:border-orange-800',
      };
    }
    if (action === 'cancelled_all') {
      const count = (payload.count as number | undefined) ?? 0;
      return {
        icon: <Trash2 className="h-4 w-4 text-red-500" />,
        title: t('notifications.reminder.cancelledAll'),
        body: count > 0 ? t('notifications.reminder.deletedCount', { count }) : undefined,
        accent: 'border-red-200 dark:border-red-800',
      };
    }
  }

  if (type === 'calendar') {
    // S4-94: продюсер (master-agent) шлёт `{eventId, title, startsAt, location}`.
    // `startsAt` — УЖЕ локализованная строка в TZ пользователя
    // (`formatLocalWallClock(...).text`), поэтому показываем её как есть:
    // парсинг в Date / `fmt` дал бы двойную конвертацию или Invalid Date.
    // Читает ту же пару полей (`title`, `startsAt`), что чат-`formatCalendar`
    // в messaging-gateway (полной симметрии нет: без title web показывает
    // fallback-название + время, чат — только «Событие создано»).
    // Исключение: без `startsAtLocal` life-planner шлёт в `startsAt` сырой
    // ISO-инстант (warn-фолбэк) — его, и только его, форматируем в локали/TZ
    // браузера, иначе юзер видит «2026-09-26T10:00:00.000Z».
    if (action === 'created') {
      const title =
        typeof payload.title === 'string' && payload.title.trim()
          ? payload.title
          : t('notifications.calendar.fallbackTitle');
      const rawWhen = typeof payload.startsAt === 'string' ? payload.startsAt.trim() : '';
      const when = !rawWhen
        ? undefined
        : isIsoInstant(rawWhen)
          ? fmt.formatDateTime(rawWhen, { dateStyle: 'medium', timeStyle: 'short' })
          : rawWhen;
      return {
        icon: <Calendar className="h-4 w-4 text-purple-500" />,
        title: t('notifications.calendar.created'),
        body: when
          ? t('notifications.calendar.createdBodyWhen', { title, when })
          : t('notifications.calendar.createdBody', { title }),
        accent: 'border-purple-200 dark:border-purple-800',
      };
    }
    // Неизвестный action календаря → silent drop (S4-45 HIGH-2), а НЕ пол
    // SSOT_TYPE_TITLES: calendar из пола убран вместе с этой веткой.
    if (import.meta.env.DEV) {
      // Та же dev-диагностика, что в конце describeEvent: ранний return
      // иначе делает новый calendar-action невидимым и для разработчика.
      console.warn(`[SystemNotificationToasts] dropped unknown calendar action: ${action}`);
    }
    return null;
  }

  if (type === 'memory') {
    // memory channel #2 — branches by action verb. Layer is informational
    // ('semantic' | 'episodic' | 'procedural'); we surface the entity hint
    // (knowledgeKey / patternName / episodeType) to keep toasts concrete.
    const layer = String(payload.layer ?? 'memory');
    const entityHint =
      (payload.knowledgeKey as string | undefined) ??
      (payload.patternName as string | undefined) ??
      (payload.episodeType as string | undefined) ??
      undefined;
    if (action === 'fact_added' || action === 'episode_added' || action === 'pattern_added') {
      return {
        icon: <Check className="h-4 w-4 text-green-500" />,
        title: t('notifications.memory.added'),
        body: entityHint ? `${layer}: ${entityHint}` : layer,
        accent: 'border-green-200 dark:border-green-800',
      };
    }
    if (action === 'fact_updated' || action === 'pattern_updated') {
      return {
        icon: <Check className="h-4 w-4 text-blue-500" />,
        title: t('notifications.memory.updated'),
        body: entityHint ? `${layer}: ${entityHint}` : layer,
        accent: 'border-blue-200 dark:border-blue-800',
      };
    }
    if (
      action === 'fact_removed' ||
      action === 'episode_removed' ||
      action === 'pattern_removed'
    ) {
      return {
        icon: <Trash2 className="h-4 w-4 text-orange-500" />,
        title: t('notifications.memory.removed'),
        body: entityHint ? `${layer}: ${entityHint}` : layer,
        accent: 'border-orange-200 dark:border-orange-800',
      };
    }
    return {
      icon: <Check className="h-4 w-4 text-green-500" />,
      title: t('notifications.memory.changed'),
      body: `${layer}/${action}`,
      accent: 'border-green-200 dark:border-green-800',
    };
  }

  if (type === 'receipt') {
    // NBF-2 Phase B (2026-05-24): receipt OCR + shopping auto-complete
    // toast — surfaces «📸 Распознал N позиций, отмечено M купленными»
    // независимо от LLM reply.
    if (action === 'processed') {
      const matched = Number(payload.matched ?? 0);
      const recognized = Number(payload.recognizedItems ?? 0);
      const unmatched = Array.isArray(payload.unmatchedItems)
        ? payload.unmatchedItems
        : [];
      const body =
        matched > 0
          ? t('notifications.receipt.matched', { count: recognized, matched })
          : recognized > 0
            ? t('notifications.receipt.noMatch', { count: recognized })
            : t('notifications.receipt.empty');
      return {
        icon: <Receipt className="h-4 w-4 text-blue-500" />,
        title:
          matched > 0
            ? t('notifications.receipt.processedTitle')
            : t('notifications.receipt.receivedTitle'),
        body:
          unmatched.length > 0 && matched > 0
            ? t('notifications.receipt.withUnmatched', {
                body,
                items: `${unmatched.slice(0, 3).join(', ')}${unmatched.length > 3 ? '…' : ''}`,
              })
            : body,
        accent: 'border-blue-200 dark:border-blue-800',
      };
    }
    return {
      icon: <Receipt className="h-4 w-4 text-blue-500" />,
      title: t('notifications.receipt.generic'),
      body: action,
      accent: 'border-blue-200 dark:border-blue-800',
    };
  }

  if (type === 'routing') {
    // routing channel #3 — agent_called / agent_returned. Show only
    // 'agent_called' as a toast: it's the "I'm working on it" signal.
    // 'agent_returned' lands silently in the buffer (still useful for
    // future sidebar log) so we don't double-toast every dispatch.
    if (action === 'agent_called') {
      const tool =
        (payload.toolName as string | undefined) ?? t('notifications.routing.fallbackCalled');
      const human = humanizeAgentTool(tool, t);
      return {
        icon: <ArrowRight className="h-4 w-4 text-purple-500" />,
        title: t('notifications.routing.calling'),
        body: human,
        accent: 'border-purple-200 dark:border-purple-800',
      };
    }
    // agent_returned — surfaced as a success/fail tick under the same
    // accent. Keeps user informed without spamming a second toast.
    if (action === 'agent_returned') {
      const ok = payload.success === true;
      const tool =
        (payload.toolName as string | undefined) ?? t('notifications.routing.fallbackReturned');
      return {
        icon: ok ? (
          <Check className="h-4 w-4 text-purple-500" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-red-500" />
        ),
        title: ok ? t('notifications.routing.returned') : t('notifications.routing.failed'),
        body: humanizeAgentTool(tool, t),
        accent: ok
          ? 'border-purple-200 dark:border-purple-800'
          : 'border-red-200 dark:border-red-800',
      };
    }
    return {
      icon: <ArrowRight className="h-4 w-4 text-purple-500" />,
      title: t('notifications.routing.generic'),
      body: action,
      accent: 'border-purple-200 dark:border-purple-800',
    };
  }

  if (type === 'smart_list') {
    // SMART-LIST-NOTIF-1 (2026-05-28): toast при addToShoppingList / addToTodoList
    // dispatch. Producer (master-agent AgenticLoopService.dispatchAdd*Items)
    // emit'ит после успешного save в life-planner.
    const listName = String(payload.listName ?? '');
    const listType = String(payload.listType ?? 'shopping');
    const items = Array.isArray(payload.items) ? (payload.items as string[]) : [];
    const count = Number(payload.count ?? items.length ?? 0);
    const isTodo = listType === 'todo';
    const verb = isTodo
      ? t('notifications.smartList.addedTodo')
      : t('notifications.smartList.addedShopping');
    const icon = isTodo ? (
      <CheckSquare className="h-4 w-4 text-emerald-500" />
    ) : (
      <ShoppingCart className="h-4 w-4 text-blue-500" />
    );
    if (action === 'item_added') {
      const itemsPreview =
        items.length > 0 ? items.slice(0, 3).join(', ') + (count > 3 ? ` (+${count - 3})` : '') : '';
      const dueAt = payload.dueAt as string | undefined;
      const dueDate = payload.dueDate as string | undefined;
      const dueSuffix = dueAt ? ` · ${dueAt}` : dueDate ? ` · ${dueDate}` : '';
      return {
        icon,
        title: listName ? t('notifications.smartList.addedNamed', { verb, list: listName }) : verb,
        body: itemsPreview ? `${itemsPreview}${dueSuffix}` : undefined,
        accent: isTodo
          ? 'border-emerald-200 dark:border-emerald-800'
          : 'border-blue-200 dark:border-blue-800',
      };
    }
    if (action === 'list_created') {
      return {
        icon,
        title: t('notifications.smartList.listCreated', {
          list: listName || t('notifications.smartList.untitled'),
        }),
        body: isTodo
          ? t('notifications.smartList.typeTodo')
          : t('notifications.smartList.typeShopping'),
        accent: isTodo
          ? 'border-emerald-200 dark:border-emerald-800'
          : 'border-blue-200 dark:border-blue-800',
      };
    }
    // S3-77 (2026-07-18): покрытие всех мутирующих list-тулов. Имя списка/
    // item'а producer сейчас передаёт НЕ всегда (delete/complete тулы знают
    // только id) — формулировка деградирует без имени, оставаясь корректной.
    const itemsPreview =
      items.length > 0 ? items.slice(0, 3).join(', ') + (count > 3 ? ` (+${count - 3})` : '') : '';
    if (action === 'item_deleted') {
      return {
        icon: <Trash2 className="h-4 w-4 text-red-500" />,
        title: listName
          ? t('notifications.smartList.itemDeletedNamed', { list: listName })
          : t('notifications.smartList.itemDeleted'),
        body: itemsPreview || undefined,
        accent: 'border-red-200 dark:border-red-800',
      };
    }
    if (action === 'item_completed') {
      return {
        icon: <Check className="h-4 w-4 text-emerald-500" />,
        title: listName
          ? t('notifications.smartList.itemCompletedNamed', { list: listName })
          : t('notifications.smartList.itemCompleted'),
        body: itemsPreview || undefined,
        accent: 'border-emerald-200 dark:border-emerald-800',
      };
    }
    if (action === 'list_deleted') {
      return {
        icon: <Trash2 className="h-4 w-4 text-red-500" />,
        title: listName
          ? t('notifications.smartList.listDeletedNamed', { list: listName })
          : t('notifications.smartList.listDeleted'),
        body: undefined,
        accent: 'border-red-200 dark:border-red-800',
      };
    }
    if (action === 'list_cleared') {
      return {
        icon: <Trash2 className="h-4 w-4 text-amber-500" />,
        title: listName
          ? t('notifications.smartList.listClearedNamed', { list: listName })
          : t('notifications.smartList.listCleared'),
        body: undefined,
        accent: 'border-amber-200 dark:border-amber-800',
      };
    }
    return {
      icon,
      title: t('notifications.smartList.generic'),
      body: action,
      accent: 'border-gray-200 dark:border-gray-700',
    };
  }

  if (type === 'budget') {
    // S9 Q1 phase 4 (2026-05-28): budget hard-cap auto-revert notify.
    // Producer (master-agent UserPreferencesService.notifyBudgetExceededOnce)
    // гарантирует at-most-once emit per период через Redis dedup-flag.
    if (action === 'budget_exceeded') {
      const current = Number(payload.currentRub ?? 0);
      const max = Number(payload.maxRub ?? 0);
      const fallback = String(payload.fallbackModel ?? 'deepseek-chat');
      // S4-35 (I4): сумма в рублях — в формате локали пользователя, со знаком валюты.
      const formatRub = (n: number) =>
        fmt.formatCurrency(n, 'RUB', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      return {
        icon: <Wallet className="h-4 w-4 text-amber-500" />,
        title: t('notifications.budget.exceededTitle'),
        body: t('notifications.budget.exceededBody', {
          spent: formatRub(current),
          max: formatRub(max),
          model: fallback,
        }),
        accent: 'border-amber-200 dark:border-amber-800',
      };
    }
    return {
      icon: <Wallet className="h-4 w-4 text-amber-500" />,
      title: t('notifications.budget.generic'),
      body: action,
      accent: 'border-amber-200 dark:border-amber-800',
    };
  }

  if (type === 'tool_summary') {
    // S4-45 HIGH-2 fix: prior to this branch, `tool_summary` fell into the
    // generic fallback below and rendered `title:'Системное событие',
    // body:'tool_summary/summary'` — noise plus a raw internal type/action
    // leak into the UI on EVERY tool-using turn (same class as S4-44: a new
    // SSOT type published but not consumed by this producer). Compact,
    // factual rendering — only what the payload actually carried (anti-
    // fabrication, mirrors messaging-gateway's chat formatter discipline).
    const tools =
      (payload.tools as Array<{ toolName: string; count: number }> | undefined) ?? [];
    const toolsLabel = tools.length
      ? tools
          .map((tool) => (tool.count > 1 ? `${tool.toolName} ×${tool.count}` : tool.toolName))
          .join(', ')
      : undefined;
    const webSearch = payload.webSearch as
      | { sources?: Array<{ title: string; url: string }> }
      | undefined;
    const sourcesCount = webSearch?.sources?.length ?? 0;
    const body =
      toolsLabel && sourcesCount > 0
        ? t('notifications.toolSummary.withSources', { tools: toolsLabel, count: sourcesCount })
        : toolsLabel;
    return {
      icon: <Bell className="h-4 w-4 text-indigo-500" />,
      title: t('notifications.toolSummary.title'),
      body,
      accent: 'border-indigo-200 dark:border-indigo-800',
    };
  }

  // S4-45 HIGH-2 (defense-in-depth): a raw `type/action` string must NEVER
  // reach the UI. But a blanket silent drop was too wide — external review
  // (2026-08-22, MEDIUM-3) showed it also swallowed `calendar` and
  // `scheduled`, which are LIVE backend-SSOT types emitted today and merely
  // lack a rich branch here. Before this component had the drop they at
  // least rendered (ugly, leaking); after it they vanished — a regression
  // against S4-94 acceptance (3) ("scheduled toasts on web are not broken").
  //
  // Split the two cases instead:
  //   • type known to the backend SSOT but not yet richly rendered →
  //     minimal, human-readable toast; NO internal identifiers.
  //   • type unknown to this build entirely → silent drop (the original
  //     HIGH-2 guarantee, kept intact).
  // S4-94: `calendar` now has its own rich branch above (title + startsAt);
  // only `scheduled` still lands on this floor.
  const knownTypeTitleKey = SSOT_TYPE_TITLES[type];
  if (knownTypeTitleKey) {
    const humanBody = typeof payload.title === 'string' ? payload.title : undefined;
    return {
      icon: <Bell className="h-4 w-4 text-gray-500" />,
      title: t(knownTypeTitleKey),
      body: humanBody,
      accent: 'border-gray-200 dark:border-gray-700',
    };
  }

  if (import.meta.env.DEV) {
    // Namespaced, dev-only: makes the drop diagnosable without shipping
    // internal plumbing to users (the exact failure mode S4-44 died of
    // twice was that the drop was invisible to developers too).
    console.warn(`[SystemNotificationToasts] dropped unknown notification type: ${type}`);
  }
  return null;
}

/**
 * Map machine tool name (`life_planner_expert`) to a domain phrase the
 * user can read ("по жизненному планированию"). Unknown tools fall back
 * to the raw name so new experts surface immediately even before the UI
 * is updated for them.
 *
 * S4-35: значения карты — ключи локали, фраза берётся `t()` в момент рендера.
 */
const AGENT_TOOL_PHRASE_KEYS: Record<string, string> = {
  life_planner_expert: 'notifications.routing.experts.lifePlanner',
  fitness_expert: 'notifications.routing.experts.fitness',
  wellness_expert: 'notifications.routing.experts.wellness',
};

function humanizeAgentTool(tool: string, t: TFunction): string {
  const key = AGENT_TOOL_PHRASE_KEYS[tool];
  return key ? t(key) : tool;
}
